import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { ArrowLeft, Search } from 'lucide-react';
import { useState } from 'react';
import DatePicker from 'react-datepicker';
import { Link } from 'react-router';
import type { PurchaseOrderListStatusFilter, PurchaseOrderStatus } from '@/api/hanly-v2/hanlyAPIV2.schemas';
import { usePurchaseOrderList } from '@/api/hanly-v2/purchase-orders/purchase-orders';
import { formatYMD, isSupportedDate, parseSupportedDate } from '@/utils/date';

const statusTabItems: { key: PurchaseOrderListStatusFilter; label: string }[] = [
  { key: 'in_progress', label: '未完了' },
  { key: 'completed', label: '完了' },
];

const getBorderColor = (status?: PurchaseOrderStatus): string => {
  if (status === 'ISSUED') return 'border-l-red-600';
  if (status === 'PARTIALLY_RECEIVED') return 'border-l-amber-300';
  return 'border-l-green-600';
};

export const ReceivingPage = () => {
  const [statusTab, setStatusTab] = useState<PurchaseOrderListStatusFilter>('in_progress');
  const [todayOnly, setTodayOnly] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // 日付範囲は暦日で送り、暦日の解釈はサーバに任せる (端末TZの日境界を日時で送ると開始日が 1 日前にずれる)
  const deliveryDateParams = (() => {
    if (todayOnly) {
      const today = format(new Date(), 'yyyy-MM-dd');
      return { delivery_date_from: today, delivery_date_to: today };
    }
    return {
      ...(fromDate ? { delivery_date_from: fromDate } : {}),
      ...(toDate ? { delivery_date_to: toDate } : {}),
    };
  })();

  const searchParams = (() => {
    const normalized = searchText.trim();
    if (!normalized) return {};
    if (/^\d+$/.test(normalized)) return { id: normalized };
    return { supplier_name: normalized };
  })();

  const { data, isLoading } = usePurchaseOrderList({
    status_filter: statusTab,
    ordering: 'delivery_date,id',
    page_size: 100,
    ...deliveryDateParams,
    ...searchParams,
  });

  const orders = (data?.results ?? []).filter(
    (order) => statusTab !== 'in_progress' || order.status !== 'DRAFT',
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="bg-white px-2 py-1">
        <Link to="/" className="flex items-center gap-2 font-bold">
          <ArrowLeft className="size-5" />
          入荷
        </Link>
      </div>

      <div className="px-5 pb-3">
        {/* ステータスタブ */}
        <div className="flex border-b border-neutral-300 mb-3">
          {statusTabItems.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setStatusTab(tab.key)}
              className={`flex-1 py-1 text-center font-bold cursor-pointer ${
                statusTab === tab.key ? 'bg-sky-800 text-white' : 'text-zinc-800'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* フィルター */}
        <div className="flex items-center gap-3 mb-1">
          <div>
            <button
              type="button"
              onClick={() => {
                const next = !todayOnly;
                setTodayOnly(next);
                if (next) {
                  const today = format(new Date(), 'yyyy-MM-dd');
                  setFromDate(today);
                  setToDate(today);
                } else {
                  setFromDate('');
                  setToDate('');
                }
              }}
              className={`shrink-0 px-3 py-1 rounded-sm cursor-pointer ${
                todayOnly ? 'bg-gray-400 text-white' : 'bg-neutral-200 text-zinc-800'
              }`}
            >
              本日入荷
            </button>
          </div>
          <div className="flex-1 flex items-center gap-x-1">
            <DatePicker
              selected={parseSupportedDate(fromDate)}
              onChange={(date: Date | null) => {
                if (date && !isSupportedDate(date)) return;
                setFromDate(date ? format(date, 'yyyy-MM-dd') : '');
                setTodayOnly(false);
              }}
              dateFormat="yyyy/MM/dd"
              locale={ja}
              maxDate={parseSupportedDate(toDate) ?? undefined}
              placeholderText="開始日"
              isClearable
              className="w-full border border-neutral-400 rounded px-2 py-1 bg-white"
            />
            <span className="text-neutral-400">-</span>
            <DatePicker
              selected={parseSupportedDate(toDate)}
              onChange={(date: Date | null) => {
                if (date && !isSupportedDate(date)) return;
                setToDate(date ? format(date, 'yyyy-MM-dd') : '');
                setTodayOnly(false);
              }}
              dateFormat="yyyy/MM/dd"
              locale={ja}
              minDate={parseSupportedDate(fromDate) ?? undefined}
              placeholderText="終了日"
              isClearable
              className="w-full border border-neutral-400 rounded px-2 py-1 bg-white"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 border border-neutral-200 rounded px-2 py-1 bg-white">
          <Search className="size-5 text-neutral-400 shrink-0" />
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="仕入先名、発注No."
            className="!border-0 !p-0 flex-1"
          />
        </div>

        {/* 発注一覧 */}
        <div className="flex flex-col gap-2 mt-3">
          {isLoading && <p className="text-sm text-neutral-500 text-center py-4">読み込み中...</p>}
          {!isLoading && orders.length === 0 && (
            <p className="text-sm text-neutral-500 text-center py-4">該当する発注はありません</p>
          )}
          {orders.map((order) => (
            <Link
              key={order.id}
              to={`/receiving/${order.id}`}
              className={`bg-white border-l-3 border-solid px-3 py-2 flex flex-col gap-1 ${getBorderColor(order.status)}`}
            >
              <div className="flex flex-col gap-0.5">
                <p className="text-xs leading-relaxed">発注No.{order.id}</p>
                <p className="text-base leading-7">{order.supplier_name}</p>
              </div>
              <div className="flex items-center">
                <span className="text-sm text-neutral-500 leading-relaxed w-20 mt-0.5">入荷予定日:</span>
                <span className="font-bold text-zinc-800">{formatYMD(order.delivery_date)}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};
